import React from 'react';
import type { NotificationOut } from 'service/notificationsService';
import type { KnownNotification } from './types';
import { GenericNotification } from './renderers/GenericNotification';

type NotificationTypeMap = { [N in KnownNotification as N['type']]: N };

// Finalize in NI-8
// Exhaustive over KnownNotification['type'], every type must have a renderer or this won't compile.
const registry: { [K in KnownNotification['type']]: (notification: NotificationTypeMap[K]) => React.ReactNode } = {
  // Allows users to build components with content from their notification payload
  'hris.load_failed': (n) => (
    <GenericNotification
      title={`HRIS load failed: ${n.type}`}
      description="Your HRIS data could not be loaded. Please try again."
      tag={{ color: 'error', children: 'Error' }}
      link={{ text: 'View', link: 'google.com' }}
    />
  ),
  'hris.load_partial': () => (
    <GenericNotification
      title="HRIS load partially completed"
      description="Some HRIS records were loaded. Review the results for details."
    />
  ),
  'survey.load_success': () => (
    <GenericNotification
      title="Survey loaded successfully"
      description="Your survey data has finished loading."
    />
  ),
  'survey.load_failed': () => (
    <GenericNotification
      title="Survey load failed"
      description="Your survey data could not be loaded. Please try again."
      tag={{ color: 'error', children: 'Error' }}
    />
  ),
  'survey.field_mapping_ready': () => (
    <GenericNotification
      title="Survey field mapping ready"
      description="Review and confirm the field mapping for your survey."
      tag={{ color: 'success', children: 'Ready' }}
      link={{ text: 'Review', link: 'google.com' }}
    />
  ),
};

// Runtime list of registered types, derived from `registry` so it can't drift.
// Single source of truth for anything needing the known-type set (e.g. the dev
// notifications sandbox).
export const KNOWN_NOTIFICATION_TYPES = Object.keys(registry) as KnownNotification['type'][];

function isKnownNotification(n: NotificationOut): n is KnownNotification {
  return n.type in registry;
}

function callRegistry<K extends KnownNotification['type']>(
  key: K,
  notification: NotificationTypeMap[K]
): React.ReactNode {
  return registry[key](notification);
}

// Defers registry invocation to render time so a parent error boundary can contain throws.
function RegistryInvocation({ notification }: { notification: KnownNotification }) {
  return <>{callRegistry(notification.type, notification)}</>;
}

export function renderNotification(notification: NotificationOut): React.ReactNode | null {
  if (!isKnownNotification(notification)) {
    console.error(`Missing notification registry entry for type: ${notification.type}`);
    return null;
  }
  return <RegistryInvocation notification={notification} />;
}
